/**
 * Ad blocker detection probe.
 *
 * Answers "is a blocker filtering THIS site?", not "is one installed?" - those are different
 * questions. Blockers keep blocking ad/tracker domains globally even on sites the user has
 * allowlisted, so a network probe cannot tell an allowlisted user apart from a filtered one,
 * and would keep warning users who have already done what we asked.
 *
 * Element hiding is switched off per-site when a user allowlists it, so it tracks what we
 * actually warn about: the extension doing work on Kerika's page.
 *
 * Nothing here leaves the page. No third-party requests, so nothing to gate on cookie consent
 * and nothing that a network outage can make look like a blocker.
 */

/**
 * Class names drawn from the generic element-hiding rules ad blocklists ship. A blocker
 * filtering this page hides the element; on an allowlisted site it is left alone.
 */
const BAIT_CLASS_NAMES = 'adsbox ad-banner ads ad-placement pub_300x250 text-ad textAd banner_ad adsbygoogle google-ad ad-container sponsor ad-wrapper ad-slot ad-unit sponsoredAd promoted';

/** Blockers apply element hiding asynchronously, so let it settle before reading the layout. */
const COSMETIC_SETTLE_IN_MS = 200;

/**
 * Renders a decoy ad-shaped element and reports whether a blocker hid it.
 * @returns {Promise<Boolean>} Resolves `true` when the element was hidden for us.
 */
const isBaitHidden = () => {
  const bait = document.createElement('div');
  bait.className = BAIT_CLASS_NAMES;
  // Off-screen but still laid out: hiding it ourselves would be indistinguishable from a
  // blocker hiding it.
  bait.style.cssText = 'position: absolute; left: -10000px; top: -10000px; width: 10px; height: 10px;';
  document.body.appendChild(bait);

  return new Promise((resolve) => {
    setTimeout(() => {
      const style = window.getComputedStyle(bait);
      const hidden =
        bait.offsetHeight === 0 ||
        bait.offsetParent === null ||
        style.display === 'none' ||
        style.visibility === 'hidden';
      bait.remove();
      resolve(hidden);
    }, COSMETIC_SETTLE_IN_MS);
  });
};

/**
 * Runs the detection probe.
 * @returns {Promise<{detected: Boolean, source: ('AD_SCRIPT'|null)}>} `detected` is `true`
 * only when a blocker is filtering this site.
 *
 * `source` is `AD_SCRIPT` to match the enum in the analytics `ad-blocker-warning` spec
 * (prd-wiki). The decoy uses ad-content class names (EasyList ad rules), so `AD_SCRIPT` is the
 * right category of the two the spec defines - though "SCRIPT" is historical: this probe hides
 * an element, it does not load a script.
 */
export const detectAdBlocker = async () => {
  const detected = await isBaitHidden();

  // This probe fails silently in both directions, so name what actually happened.
  console.debug('detectAdBlocker > bait element hidden: ', detected);

  return { detected, source: detected ? 'AD_SCRIPT' : null };
};
